import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { selectionToIssueTypeEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import { useIssueTypePool, type IssueTypeInput } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type IssueTypeSelectorProps = Omittable<{
  /** Available issue types */
  issueTypes: IssueTypeInput[];
  /** Currently selected issue type ID */
  value?: string | null;
  /** Called when an issue type is selected */
  onValueChange?: (issueTypeId: string) => void;
  /** Name of a deleted issue type (shows as disabled entry) */
  deletedIssueTypeName?: string | null;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  /** Render the selected type in the bordered value trigger with a muted
   *  label (the issue-view summary's quiet inline field). */
  valueMuted?: boolean;
  children?: React.ReactNode;
}>;

export function IssueTypeSelector({
  issueTypes,
  value,
  onValueChange,
  deletedIssueTypeName,
  variant,
  placeholder = "Select type...",
  size,
  disabled,
  valueMuted,
  children,
}: IssueTypeSelectorProps) {
  const { pool } = useIssueTypePool({
    issueTypes,
    deletedIssueTypeName,
    selectedIssueTypeId: value,
  });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 1,
      searchPlaceholder: "Search project types...",
      emptyMessage: "No matching project types",
      noItemsMessage: "No project types yet",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    const entity = pool.items.find((item) => item.id === value);
    if (!entity) return [];
    return [{ id: value, poolId: pool.id, entity }];
  }, [value, pool]);

  // Veto all internal state changes — fire onValueChange instead.
  // The display only updates when the parent changes the `value` prop.
  const handleBeforeSelect = useCallback(
    (entity: { id: string }) => {
      onValueChange?.(entity.id);
      return false;
    },
    [onValueChange],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onBeforeSelect={handleBeforeSelect}>
      {children ?? (
        <>
          {valueMuted ? (
            <SelectionEntityTrigger
              toEntity={selectionToIssueTypeEntity}
              placeholder={placeholder}
              title="Type"
              size={size}
              disabled={disabled}
              showChevron
              buttonVariant="outline"
              valueMuted
            />
          ) : (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
            />
          )}
          <EntitySelectorContent size="sm">
            <EntitySelectorInput />
            <EntitySelectorPool value={pool.id} />
            <EntitySelectorEmpty>No matching project types</EntitySelectorEmpty>
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
